import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
// CSS imports for github-markdown will be moved to index.css
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { AVAILABLE_THEMES } from '@/context/ThemeContext';

// Placeholder for a function to fetch file list and content
// In a real app, this would involve API calls to a backend
// or using a bundler feature like Vite's import.meta.glob

interface DocFile {
  name: string;
  path: string; // relative path from the /src/docs directory
}

const DocumentationPage: React.FC = () => {
  const [docFiles, setDocFiles] = useState<DocFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<DocFile | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoadingList, setIsLoadingList] = useState<boolean>(true);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { theme: activeThemeName } = useTheme();
  const currentAppThemeInfo = AVAILABLE_THEMES.find(t => t.value === activeThemeName);
  const isAppCurrentlyDark = currentAppThemeInfo ? currentAppThemeInfo.isDark : false;

  // Simulate fetching list of markdown files from /src/docs/
  // We will use a placeholder for actual file system reading.
  // You mentioned we can create an index file if needed. For now,
  // let's assume we can get a list of files.
  // In a real Vite/Next.js app, you might use import.meta.glob or similar.
  const fetchDocFiles = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      // This is a placeholder. In a real app, you'd fetch this list.
      // For now, we'll hardcode a few examples.
      // You'll need to replace this with actual logic to list files from /src/docs.
      // This could be a backend endpoint or a build-time generated list.
      const files: DocFile[] = [
        { name: "Firestore Data Model", path: "1-firestore-data-model.md" },
        { name: "Backend Cloud Functions", path: "2-backend-cloud-functions.md" },
        { name: "Backend Summary", path: "2a-backend-cloud-functions-summary.md" },
        { name: "Frontend State (RTK)", path: "3-frontend-state-management-rtk.md" },
        { name: "Algolia Search Integration", path: "4-algolia-search-integration.md" },
        { name: "Frontend Redux Interaction Guide", path: "5-frontend-redux-interaction-guide.md" },
        { name: "Algolia Queue Sync", path: "algolia-queue-batch-sync.md" },
        { name: "Cost Comparison", path: "cost-comparison.md" },
        { name: "Data Import Guide", path: "data-import-guide.md" },
        { name: "Editor Interactions", path: "editor-interactions-guide.md" },
        { name: "Firestore Rules", path: "firestore-rules.md" },
        { name: "Permission Locked Components", path: "permission-locked-components.md" },
      ];
      // Simulate a delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setDocFiles(files);
    } catch (err) {
      console.error("Failed to fetch doc files:", err);
      setError("Could not load documentation list.");
      toast.error("Could not load documentation list.");
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const fetchMarkdownContent = useCallback(async (filePath: string) => {
    if (!filePath) return;
    setIsLoadingContent(true);
    setError(null);
    try {
      // In a Vite project, you could use dynamic imports for .md files if they are in /public
      // or use a raw loader: import mdContent from './path/to/file.md?raw';
      // For /src/docs, you'd likely need a backend endpoint or a build step
      // that makes these files available to the client.

      // Placeholder: Fetch content. Replace with actual fetch from /src/docs/${filePath}
      // This will likely require you to set up a way to serve these files or bundle them.
      const response = await fetch(`/docs/${filePath}`); // Assumes files are served from /public/docs or similar
      if (!response.ok) {
        throw new Error(`Failed to load ${filePath}: ${response.statusText}`);
      }
      const text = await response.text();
      setMarkdownContent(text);
    } catch (err) {
      console.error(`Failed to fetch markdown content for ${filePath}:`, err);
      setError(`Could not load content for ${selectedFile?.name || 'the selected file'}.`);
      toast.error(`Could not load content for ${selectedFile?.name || 'the selected file'}.`);
      setMarkdownContent(''); // Clear content on error
    } finally {
      setIsLoadingContent(false);
    }
  }, [selectedFile?.name]);


  useEffect(() => {
    fetchDocFiles();
  }, [fetchDocFiles]);

  useEffect(() => {
    if (selectedFile) {
      fetchMarkdownContent(selectedFile.path);
    } else {
      setMarkdownContent(''); // Clear content if no file is selected
    }
  }, [selectedFile, fetchMarkdownContent]);

  const handleFileSelect = (file: DocFile) => {
    setSelectedFile(file);
  };

  return (
    <PermissionGuard requiredPermission="view_technical">
      <div className="h-full flex flex-col p-4 md:p-6 mx-auto w-full max-w-[95%] overflow-hidden">
        <Toaster />
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle>Application Documentation</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col md:flex-row gap-6 overflow-hidden">
            <aside className="w-full md:w-1/4 md:min-w-[250px] md:border-r md:pr-4">
              <h3 className="text-lg font-semibold mb-2">Documents</h3>
              {isLoadingList ? (
                <p>Loading documents...</p>
              ) : error && docFiles.length === 0 ? (
                <p className="text-red-500">{error}</p>
              ) : (
                <ScrollArea className="h-[200px] md:h-[calc(100vh-200px)]">
                  <ul className="space-y-1">
                    {docFiles.map((file) => (
                      <li key={file.path}>
                        <button
                          onClick={() => handleFileSelect(file)}
                          className={`w-full text-left p-2 rounded-md hover:bg-muted ${selectedFile?.path === file.path ? 'bg-muted font-semibold' : ''}`}
                        >
                          {file.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              )}
            </aside>
            <main className="w-full md:w-3/4 flex-1 flex flex-col overflow-hidden">
              {selectedFile ? (
                <>
                  <h3 className="text-xl font-semibold mb-3 border-b pb-2">{selectedFile.name}</h3>
                  {isLoadingContent ? (
                    <p>Loading content...</p>
                  ) : error && !markdownContent ? (
                     <p className="text-red-500">{error}</p>
                  ) : (
                    <div className="flex-1 min-h-0">
                      <ScrollArea className="h-full w-full">
                        <div 
                          className={`${isAppCurrentlyDark ? 'markdown-body-dark' : 'markdown-body-light'} p-6 overflow-hidden`}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                            components={{
                              table: ({ node, ...props }) => (
                                <div className="overflow-auto max-w-full">
                                  <table {...props} className="my-4 border-collapse" />
                                </div>
                              )
                            }}
                          >
                            {markdownContent}
                          </ReactMarkdown>
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Select a document to view its content.</p>
                </div>
              )}
            </main>
          </CardContent>
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default DocumentationPage; 